import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { api, toISODate } from "@/lib/noleggio";
import { MonthHeader, WeekdayRow, monthGrid } from "@/components/AvailabilityCalendar";

const ACTIVE = ["confirmed", "pending_payment", "completed"];

export default function AdminCalendar() {
  const today = toISODate(new Date());
  const [cursor, setCursor] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [day, setDay] = useState(today);
  const q = useQuery({ queryKey: ["admin-bookings", "all"], queryFn: () => api("/admin/bookings") });
  const cells = monthGrid(cursor.getFullYear(), cursor.getMonth());

  const byDay = useMemo(() => {
    const map = {};
    for (const b of q.data ?? []) {
      if (!ACTIVE.includes(b.status)) continue;
      const s = new Date(`${b.start.slice(0, 10)}T00:00`);
      const e = b.end.slice(0, 10);
      for (let d = s; toISODate(d) <= e; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) (map[toISODate(d)] ??= []).push(b);
    }
    return map;
  }, [q.data]);
  const list = byDay[day] ?? [];

  return (
    <div className="grid md:grid-cols-[1fr_1fr] gap-4 mt-4 [&>*]:min-w-0">
      <div data-testid="admin-calendar" className="mn-card p-4">
        <MonthHeader cursor={cursor} setCursor={setCursor} prefix="admin-calendar" />
        <WeekdayRow />
        <div className="grid grid-cols-7 gap-1 relative">
          {cells.map((iso, i) => {
            if (!iso) return <span key={`b${i}`} />;
            const n = byDay[iso]?.length ?? 0;
            const on = iso === day;
            return (
              <button key={iso} data-testid={`admin-day-${iso}`} onClick={() => setDay(iso)}
                className={`h-14 rounded-xl border flex flex-col items-center justify-center transition-colors ${on ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]" : n ? "bg-[var(--brand-tertiary)] border-transparent hover:border-[var(--brand)]" : "border-transparent hover:bg-[var(--surface-2)]"} ${iso === today && !on ? "!border-[var(--brand)]" : ""}`}>
                <span className="text-[15px] font-extrabold leading-none">{Number(iso.slice(8))}</span>
                {n > 0 && <span data-testid={`admin-day-count-${iso}`} className={`text-[10px] font-bold mt-1 leading-none ${on ? "" : "text-[var(--brand-primary)]"}`}>{n} bici</span>}
              </button>
            );
          })}
          {q.isLoading && <div className="absolute inset-0 bg-white/70 flex items-center justify-center"><Loader2 className="animate-spin" color="var(--brand-primary)" /></div>}
        </div>
      </div>
      <div className="mn-card p-4">
        <p data-testid="admin-day-title" className="font-extrabold mb-3">
          {new Date(`${day}T12:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })} · {list.length} {list.length === 1 ? "bici fuori" : "bici fuori"}
        </p>
        {list.length === 0 ? <p className="text-sm text-[var(--muted)]">Nessun noleggio in questo giorno</p> : (
          <div data-testid="admin-day-list" className="flex flex-col divide-y divide-[var(--border)]">
            {list.map((b) => {
              const first = b.start.slice(0, 10) === day;
              const last = b.end.slice(0, 10) === day;
              return (
                <div key={b.id} data-testid={`admin-day-booking-${b.id}`} className="py-2.5 flex gap-3 items-start">
                  <span className="w-10 h-8 rounded-lg bg-[var(--brand-secondary)] text-[var(--brand-primary)] font-extrabold text-sm flex items-center justify-center shrink-0">{b.size}</span>
                  <div className="flex-1 min-w-0 text-sm">
                    <p className="font-bold truncate">{b.bike_name}{b.unit_id ? ` · ${b.unit_id}` : ""}</p>
                    <p className="text-[var(--text-2)] truncate">{b.user_name}{b.phone ? ` · ${b.phone}` : ""}</p>
                  </div>
                  <span className={`text-[11px] font-bold px-2 py-1 rounded-full shrink-0 ${first ? "bg-green-50 text-[var(--success)]" : last ? "bg-amber-50 text-[#c97a00]" : "bg-[var(--surface-2)] text-[var(--text-2)]"}`}>
                    {first && last ? "Ritiro e riconsegna" : first ? "Ritiro" : last ? "Riconsegna" : "In uso"}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
